import AccessTimeIcon from "@mui/icons-material/AccessTime";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import CachedOutlinedIcon from "@mui/icons-material/CachedOutlined";
import DangerousOutlinedIcon from "@mui/icons-material/DangerousOutlined";
import DoNotDisturbAltIcon from "@mui/icons-material/DoNotDisturbAlt";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import HowToRegOutlinedIcon from "@mui/icons-material/HowToRegOutlined";
import LayersOutlinedIcon from "@mui/icons-material/LayersOutlined";
import PauseCircleOutlineOutlinedIcon from "@mui/icons-material/PauseCircleOutlineOutlined";
import PendingOutlinedIcon from "@mui/icons-material/PendingOutlined";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import ReplayIcon from "@mui/icons-material/Replay";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import TaskAltRoundedIcon from "@mui/icons-material/TaskAltRounded";
import ViewInArOutlinedIcon from "@mui/icons-material/ViewInArOutlined";
import { MuiTypography } from "../typography/MuiTypography";
import { StatusBadgeWrapper } from "./StatusBadge.style";

export const STATUS_BADGE_TYPE = {
  active: "Status.Active",
  inActive: "Status.Inactive",
  open: "Status.Open",
  closed: "Status.Closed",
  inProgress: "Status.InProgress",
  expired: "Status.Expired",
  hold: "Status.Hold",
  cancelled: "Status.Cancelled",
  archived: "Status.Archived",
  assignedEnrollment: "Status.AssignedEnrollment",
  ineligible: "Status.Ineligible",
  registered: "Status.Registered",
  notRegistered: "Status.NotRegistered",
  unableToValidate: "Status.UnableToValidate",
  createdInError: "Status.CreatedInError",
  all: "Status.All",
  conCon: "Status.ConCon",
  unBilled: "Status.Unbilled",
  invoiced: "Status.Invoiced",
  reversed: "Status.Reversed",
  billed: "Status.Billed",
  scheduled: "Status.Scheduled",
  executed: "Status.Executed",
  inQueue: "Status.InQueue",
  completed: "Status.Completed",
  reOpen: "Status.ReOpen",
  noLongerValid: "Status.NoLongerValid",
  awaitingEffectiveDate: "Status.AwaitingEffectiveDate",
  autoRenewed: "Status.AutoRenewed",
  notYetStarted: "Status.NotYetStarted",
  willNotComplete: "Status.WillNotComplete",
} as const;

export type StatusBadgeType =
  (typeof STATUS_BADGE_TYPE)[keyof typeof STATUS_BADGE_TYPE];

interface StatusBadgeProps {
  status: StatusBadgeType;
  label: string;
  showLabel?: boolean;
}
const statusIconMap: Record<StatusBadgeType, React.JSX.Element> = {
  [STATUS_BADGE_TYPE.active]: <TaskAltIcon className="icon" />,
  [STATUS_BADGE_TYPE.inActive]: <DoNotDisturbAltIcon className="icon" />,
  [STATUS_BADGE_TYPE.notYetStarted]: <AccessTimeRoundedIcon className="icon" />,
  [STATUS_BADGE_TYPE.willNotComplete]: (
    <DoNotDisturbAltIcon className="rotated-icon" />
  ),
  [STATUS_BADGE_TYPE.unableToValidate]: (
    <DoNotDisturbAltIcon className="icon" />
  ),
  [STATUS_BADGE_TYPE.open]: <RadioButtonUncheckedIcon className="icon" />,
  [STATUS_BADGE_TYPE.closed]: <HighlightOffIcon className="icon" />,
  [STATUS_BADGE_TYPE.inProgress]: <PendingOutlinedIcon className="icon" />,
  [STATUS_BADGE_TYPE.expired]: <AccessTimeIcon className="icon" />,
  [STATUS_BADGE_TYPE.hold]: <PauseCircleOutlineOutlinedIcon className="icon" />,
  [STATUS_BADGE_TYPE.cancelled]: <DangerousOutlinedIcon className="icon" />,
  [STATUS_BADGE_TYPE.archived]: <LayersOutlinedIcon className="icon" />,
  [STATUS_BADGE_TYPE.assignedEnrollment]: (
    <HowToRegOutlinedIcon className="icon" />
  ),
  [STATUS_BADGE_TYPE.ineligible]: <HighlightOffIcon className="icon" />,
  [STATUS_BADGE_TYPE.createdInError]: <HighlightOffIcon className="icon" />,
  [STATUS_BADGE_TYPE.registered]: <TaskAltIcon className="icon" />,
  [STATUS_BADGE_TYPE.notRegistered]: <DoNotDisturbAltIcon className="icon" />,
  [STATUS_BADGE_TYPE.all]: <ViewInArOutlinedIcon className="icon" />,
  [STATUS_BADGE_TYPE.conCon]: <CachedOutlinedIcon className="icon" />,
  [STATUS_BADGE_TYPE.unBilled]: <AccessTimeIcon className="icon" />,
  [STATUS_BADGE_TYPE.invoiced]: <TaskAltIcon className="icon" />,
  [STATUS_BADGE_TYPE.reversed]: <ReplayIcon className="icon" />,
  [STATUS_BADGE_TYPE.billed]: <TaskAltIcon className="icon" />,
  [STATUS_BADGE_TYPE.scheduled]: <AccessTimeIcon className="icon" />,
  [STATUS_BADGE_TYPE.executed]: <TaskAltRoundedIcon className="icon" />,
  [STATUS_BADGE_TYPE.inQueue]: <AccessTimeIcon className="icon" />,
  [STATUS_BADGE_TYPE.completed]: <TaskAltIcon className="icon" />,
  [STATUS_BADGE_TYPE.reOpen]: <></>,
  [STATUS_BADGE_TYPE.noLongerValid]: <DoNotDisturbAltIcon className="icon" />,
  [STATUS_BADGE_TYPE.awaitingEffectiveDate]: (
    <RadioButtonUncheckedIcon className="icon" />
  ),
  [STATUS_BADGE_TYPE.autoRenewed]: <CachedOutlinedIcon className="icon" />,
};

export const StatusBadge = ({
  status,
  label,
  showLabel = true,
}: StatusBadgeProps) => {
  const Icon = statusIconMap[status];
  return (
    <StatusBadgeWrapper status={status}>
      {Icon}
      {showLabel && (
        <MuiTypography variant="h3Bold" className="message">
          {label}
        </MuiTypography>
      )}
    </StatusBadgeWrapper>
  );
};
